/**
 * Shared zod schemas for the CRM contracts. The SmartViewQuery shape is
 * the source of truth for both `applySmartView` (server) and the smart-view
 * editor UI (client). Every filter is a bound parameter — no string concat.
 */

import { z } from "zod";

export const crmIdParams = z.object({ id: z.coerce.number().int() });

export const smartViewQuerySchema = z.object({
  owner_actor_id: z
    .union([z.literal("me"), z.number(), z.null()])
    .optional(),
  status_slugs: z.array(z.string()).optional(),
  source_slugs: z.array(z.string()).optional(),
  tag_slugs: z.array(z.string()).optional(),
  organization_id: z.number().optional(),
  last_activity_before: z.string().optional(),
  last_activity_after: z.string().optional(),
  created_after: z.string().optional(),
  created_before: z.string().optional(),
  value_minor_gte: z.number().optional(),
  value_minor_lte: z.number().optional(),
  closed_won: z.boolean().optional(),
  closed_lost: z.boolean().optional(),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});

export type SmartViewQueryShape = z.infer<typeof smartViewQuerySchema>;

export const crmLeadRowSchema = z.object({
  id: z.number(),
  // Synced object id — already returned by the `SELECT *` reads; surfaced here
  // (additive, optional) so list rows can open the scope-free object page at
  // /o/<uid> without a section-deep, tier-dependent detail route.
  uid: z.string().optional(),
  workspace_id: z.number(),
  display_name: z.string(),
  primary_person_id: z.number().nullable(),
  primary_organization_id: z.number().nullable(),
  status_id: z.number().nullable(),
  source_id: z.number().nullable(),
  owner_actor_id: z.number().nullable(),
  last_activity_at: z.string().nullable(),
  next_action_at: z.string().nullable(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by_actor_id: z.number().nullable(),
});

export const crmOpportunityRowSchema = z.object({
  id: z.number(),
  // Synced object id — returned by SELECT * on crm_opportunities; surfaced
  // here (additive, optional) so board cards and list rows can open /o/<uid>
  // without a section-deep detail route. Same shape as crmLeadRowSchema.uid.
  uid: z.string().optional(),
  workspace_id: z.number(),
  lead_id: z.number(),
  pipeline_id: z.number(),
  stage_id: z.number(),
  name: z.string(),
  value_minor: z.number(),
  currency: z.string(),
  probability_pct: z.number().nullable(),
  expected_close_date: z.string().nullable(),
  closed_at: z.string().nullable(),
  closed_won: z.number(),
  lost_reason: z.string().nullable(),
  owner_actor_id: z.number().nullable(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by_actor_id: z.number().nullable(),
});
